import paths from "@/constants/paths";
import { useActivityTracker } from "@/context/ActivityTrackerProvider";
import { useFeatureText } from "@/context/FeatureTextProvider";
import { useMspStore } from "@/lib/zustand";
import Form, { FormField } from "@/ui-lib/components/Form";
import LoadingSpinner from "@/ui-lib/components/LoadingSpinner";
import ParsedText from "@/ui-lib/components/ParsedText";
import TextInput from "@/ui-lib/components/TextInput";
import getMergeState from "@/utils/getMergeState";
import { useTheme } from "@emotion/react";
import { getCurrencyDisplayName } from "@ternary/api-lib/analytics/utils/NumberFormatUtils";
import { CurrencyCode } from "@ternary/api-lib/constants/enums";
import {
  TenantEntity,
  TenantStatus,
  TenantType,
} from "@ternary/api-lib/core/types/Tenant";
import { actions } from "@ternary/api-lib/telemetry";
import Box from "@ternary/api-lib/ui-lib/components/Box";
import Button from "@ternary/api-lib/ui-lib/components/Button";
import Flex from "@ternary/api-lib/ui-lib/components/Flex";
import Text from "@ternary/web-ui-lib/components/Text";
import React, { ChangeEvent, MouseEvent, useEffect, useState } from "react";
import { Input } from "../../../types";
import Select from "../../../ui-lib/components/Select";
import { LabeledSwitch } from "../../../ui-lib/components/Switch";
import _copyText from "../copyText";

type Tenant = Pick<
  TenantEntity,
  | "id"
  | "preferredCurrencyCode"
  | "emailDomain"
  | "isInternal"
  | "name"
  | "status"
  | "type"
>;

interface Props {
  isInternalMode?: boolean;
  isProcessing: boolean;
  selectedTenant?: Tenant;
  onInteraction: (interaction: TenantForm.Interaction) => void;
}

interface State {
  emailDomainInput: Input<string>;
  isInternalInput: Input<boolean>;
  nameInput: Input<string>;
  preferredCurrencyCodeInput: Input<CurrencyCode>;
  statusInput: Input<TenantStatus | string>;
  typeInput: Input<TenantType | string>;
}

function TenantForm(props: Props): React.JSX.Element {
  const activityTracker = useActivityTracker();
  const theme = useTheme();
  const mspStore = useMspStore();
  const isUpdateMode = !!props.selectedTenant;

  const initialState = {
    emailDomainInput: { value: "", isValid: false, hasChanged: false },
    isInternalInput: { value: false, isValid: true, hasChanged: false },
    nameInput: { value: "", isValid: false, hasChanged: false },
    preferredCurrencyCodeInput: {
      value: CurrencyCode.USD,
      isValid: true,
      hasChanged: false,
    },
    statusInput: { value: "", isValid: true, hasChanged: false },
    typeInput: {
      value: "",
      isValid: !isUpdateMode && props.isInternalMode ? false : true,
      hasChanged: false,
    },
  };

  const { copyText } = useFeatureText(_copyText);

  const [state, setState] = useState<State>(initialState);

  const mergeState = getMergeState(setState);

  useEffect(() => {
    const updatedState = {
      emailDomainInput: props.selectedTenant
        ? {
            value: props.selectedTenant.emailDomain,
            isValid: true,
            hasChanged: false,
          }
        : { value: "", isValid: false, hasChanged: false },
      nameInput: props.selectedTenant
        ? { value: props.selectedTenant.name, isValid: true, hasChanged: false }
        : { value: "", isValid: false, hasChanged: false },
      preferredCurrencyCodeInput: props.selectedTenant
        ? {
            value: props.selectedTenant.preferredCurrencyCode,
            isValid: true,
            hasChanged: false,
          }
        : { value: CurrencyCode.USD, isValid: true, hasChanged: false },
      statusInput: props.selectedTenant
        ? {
            value: props.selectedTenant.status,
            isValid: true,
            hasChanged: false,
          }
        : { value: "", isValid: true, hasChanged: false },
      typeInput: {
        value: "",
        isValid: !isUpdateMode && props.isInternalMode ? false : true,
        hasChanged: false,
      },
    };
    mergeState(updatedState);
  }, [props.selectedTenant]);

  //
  // Interaction Handlers
  //

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    const { name, value } = event.target;

    let isValid = false;
    let hasChanged = false;

    switch (name) {
      case "preferredCurrencyCode": {
        isValid = value.length > 0;
        hasChanged = value !== props.selectedTenant?.preferredCurrencyCode;

        // Track currency change in tenant creation/update
        if (hasChanged) {
          activityTracker.captureAction(
            actions.SELECT_ADMIN_NEW_TENANT_CURRENCY_CHANGE,
            {
              current: value,
              previous: props.selectedTenant?.preferredCurrencyCode,
            }
          );
        }
        break;
      }
      case "emailDomain": {
        // Regex validation for domain pattern (allows multiple dot-separated segments)
        const emailDomainRegex = /^[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)+$/;
        isValid = value.length > 0 && emailDomainRegex.test(value);
        hasChanged = value !== props.selectedTenant?.emailDomain;
        break;
      }
      case "name": {
        isValid = value.length > 0;
        hasChanged = value !== props.selectedTenant?.name;
        break;
      }
      case "status": {
        isValid = value.length > 0;
        hasChanged = value !== props.selectedTenant?.status;
        break;
      }
      case "type": {
        isValid = value.length > 0;
        hasChanged = value !== props.selectedTenant?.type;
        break;
      }
    }

    mergeState({ [`${name}Input`]: { value, isValid, hasChanged } });
  }

  function handleChangeIsInternal(checked: boolean) {
    mergeState({
      isInternalInput: {
        value: checked,
        isValid: true,
        hasChanged: checked !== props.selectedTenant?.isInternal,
      },
    });
  }

  function handleSubmitUpdate(event: MouseEvent) {
    event.preventDefault();

    if (!props.selectedTenant) return;

    props.onInteraction({
      type: TenantForm.INTERACTION_SUBMIT_BUTTON_CLICKED_UPDATE,
      tenantID: props.selectedTenant.id,
      ...(state.emailDomainInput.hasChanged
        ? { emailDomain: state.emailDomainInput.value }
        : {}),
      ...(state.nameInput.hasChanged ? { name: state.nameInput.value } : {}),
      ...(state.preferredCurrencyCodeInput.hasChanged
        ? { preferredCurrencyCode: state.preferredCurrencyCodeInput.value }
        : {}),
      ...(state.statusInput.hasChanged
        ? { status: state.statusInput.value as TenantStatus }
        : {}),
    });
  }

  function handleSubmitCreate(event: MouseEvent): void {
    event.preventDefault();

    // If in Internal Admin, user can select a type.
    // If in MSP Admin, type defaults to MSP_CUSTOMER.
    props.onInteraction({
      type: TenantForm.INTERACTION_SUBMIT_BUTTON_CLICKED_CREATE,
      emailDomain: state.emailDomainInput.value,
      isInternal: state.isInternalInput.value,
      name: state.nameInput.value,
      preferredCurrencyCode: state.preferredCurrencyCodeInput.value,
      status: state.statusInput.value as TenantStatus,
      tenantType: props.isInternalMode
        ? (state.typeInput.value as TenantType)
        : TenantType.MSP_CUSTOMER,
    });
  }

  //
  // Validations
  //

  const everyIsValid = Object.values(state).every(
    (input: Input) => input.isValid
  );

  const someHasChanged = Object.values(state).some(
    (input: Input) => input.hasChanged
  );

  const canSubmit = isUpdateMode
    ? everyIsValid && someHasChanged
    : everyIsValid;

  const currencyOptions = Object.values(CurrencyCode).map((code) => ({
    label: getCurrencyDisplayName(code),
    value: code,
  }));

  const typeOptions = [TenantType.DIRECT_CUSTOMER, TenantType.MSP_PARENT].map(
    (type) => ({
      label: copyText[`tenantTypeOption_${type}`],
      value: type,
    })
  );

  const statusOptions: { label: string; value: TenantStatus }[] = [
    { label: copyText.tenantStatusLabel_ACTIVE, value: TenantStatus.ACTIVE },
    {
      label: copyText.tenantStatusLabel_ACTIVE_POV,
      value: TenantStatus.ACTIVE_POV,
    },
  ];

  // Should be able to update to INACTIVE if needed.
  if (isUpdateMode) {
    statusOptions.push({
      label: copyText.tenantStatusLabel_INACTIVE,
      value: TenantStatus.INACTIVE,
    });
  }

  const selectedCurrencyOption = currencyOptions.find(
    (option) => option.value === state.preferredCurrencyCodeInput.value
  );

  const selectedStatusOption = statusOptions.find(
    (option) => option.value === state.statusInput.value
  );

  const selectedTypeOption = typeOptions.find(
    (option) => option.value === state.typeInput.value
  );

  return (
    <Box width={!props.isInternalMode ? "40%" : ""}>
      <Text marginBottom={theme.space_md} appearance="h4">
        {isUpdateMode
          ? copyText.formTitleInformation
          : copyText.formTitleCreate}
      </Text>
      <Form>
        <FormField
          name="name"
          disabled={props.isProcessing}
          input={TextInput}
          label={copyText.nameInputLabel}
          value={state.nameInput.value}
          onChange={handleChange}
        />
        {props.isInternalMode && !isUpdateMode && (
          <FormField label={copyText.typeInputLabel}>
            <Select
              options={typeOptions}
              value={selectedTypeOption}
              onChange={(option) =>
                handleChange({
                  target: { name: "type", value: option?.value },
                } as ChangeEvent<HTMLInputElement>)
              }
            />
          </FormField>
        )}
        <FormField label={copyText.statusInputLabel}>
          <Select
            options={statusOptions}
            value={selectedStatusOption}
            onChange={(option) =>
              handleChange({
                target: { name: "status", value: option?.value },
              } as ChangeEvent<HTMLInputElement>)
            }
          />
        </FormField>
        <FormField
          name="emailDomain"
          disabled={props.isProcessing}
          input={TextInput}
          label={copyText.emailDomainInputLabel}
          placeholder={copyText.emailDomainInputPlaceholder}
          value={state.emailDomainInput.value}
          onChange={handleChange}
        />
        <FormField label={copyText.currencyCodeInputLabel}>
          <Select
            options={currencyOptions}
            value={selectedCurrencyOption}
            onChange={(option) =>
              handleChange({
                target: { name: "preferredCurrencyCode", value: option?.value },
              } as ChangeEvent<HTMLInputElement>)
            }
          />
        </FormField>
      </Form>
      {props.isInternalMode && !isUpdateMode && (
        <Flex justifyContent="flex-end" marginBottom={theme.space_md}>
          <LabeledSwitch
            checked={state.isInternalInput.value}
            label={copyText.isInternalLabel}
            onChange={handleChangeIsInternal}
          />
        </Flex>
      )}
      {!props.isInternalMode && (
        <Box marginBottom={theme.space_lg}>
          <Text appearance="h4">{copyText.integrationsSettingHeader}</Text>
          <Text
            color={theme.text_color_caption}
            marginTop={theme.space_xxs}
            fontSize={theme.fontSize_ui}
          >
            <ParsedText
              text={copyText.integrationsSettingCaption}
              components={{
                link: (
                  <a
                    href={`${paths._mspAdmin}?tenant_id=${mspStore.selectedParentTenantID}&tab=settings&custom=UpdateCustom`}
                    rel="noreferrer"
                    target="_blank"
                  />
                ),
              }}
            />
          </Text>
        </Box>
      )}
      <Button
        disabled={!canSubmit || props.isProcessing}
        primary
        type="submit"
        width={100}
        onClick={isUpdateMode ? handleSubmitUpdate : handleSubmitCreate}
      >
        {props.isProcessing ? <LoadingSpinner /> : copyText.submitButtonLabel}
      </Button>
    </Box>
  );
}

TenantForm.INTERACTION_CANCEL_BUTTON_CLICKED =
  "TenantForm.INTERACTION_CANCEL_BUTTON_CLICKED" as const;
TenantForm.INTERACTION_SUBMIT_BUTTON_CLICKED_CREATE =
  "TenantForm.INTERACTION_SUBMIT_BUTTON_CLICKED_CREATE" as const;
TenantForm.INTERACTION_SUBMIT_BUTTON_CLICKED_UPDATE =
  "TenantForm.INTERACTION_SUBMIT_BUTTON_CLICKED_UPDATE" as const;

type InteractionCancelButtonClicked = {
  type: typeof TenantForm.INTERACTION_CANCEL_BUTTON_CLICKED;
};

type InteractionSubmitButtonClickedCreate = {
  type: typeof TenantForm.INTERACTION_SUBMIT_BUTTON_CLICKED_CREATE;
  emailDomain: string;
  isInternal: boolean;
  name: string;
  preferredCurrencyCode: CurrencyCode;
  status: TenantStatus;
  tenantType: TenantType;
};

type InteractionSubmitButtonClickedUpdate = {
  type: typeof TenantForm.INTERACTION_SUBMIT_BUTTON_CLICKED_UPDATE;
  tenantID: string;
  emailDomain?: string;
  name?: string;
  preferredCurrencyCode?: CurrencyCode;
  status?: TenantStatus;
};

// eslint-disable-next-line @typescript-eslint/no-namespace
namespace TenantForm {
  export type Interaction =
    | InteractionCancelButtonClicked
    | InteractionSubmitButtonClickedCreate
    | InteractionSubmitButtonClickedUpdate;
}

export default TenantForm;
